/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AppRouter } from "@/server/app/trpc/trpc.router";
import { datadogLogs } from "@datadog/browser-logs";
import { RECAPTCHA_HEADERS, SERVER_TRPC_BASE_PATH } from "@simplyk/common";
import { TRPCErrorData } from "@simplyk/common/lib/helpers/trpc";
import { httpLink, OperationLink, TRPCClientError, TRPCClientErrorLike, TRPCRequestOptions } from "@trpc/client";
import { createTRPCNext } from "@trpc/next";
import { AnyRouter } from "@trpc/server";
import { observable } from "@trpc/server/observable";

export type AppRouterType = AppRouter;

// needed to fix a typing error because of the server import
declare module "express" {
  interface Request {
    user?: unknown;
  }
}

export const assertTRPCErrorData = <E>(response: unknown): { error: TRPCErrorData<E> } | null => {
  if (!response || typeof response !== "object") {
    return null;
  }

  const maybeError = (response as { error: TRPCErrorData<E> })?.error || response;
  if (!maybeError || typeof maybeError !== "object") {
    return null;
  }

  return { error: maybeError };
};

const errorLink = <TRouter extends AnyRouter>(): OperationLink<TRouter> => {
  return ({ next, op }) => {
    return observable((observer) => {
      return next(op).subscribe({
        next: (result) => {
          observer.next(result);
        },
        error: (err: TRPCClientErrorLike<TRouter>) => {
          if ((err.data as { httpStatus?: number })?.httpStatus === 401) {
            datadogLogs.logger.warn("User has received a 401 response from tRPC", {
              path: op.path,
            });
            // Dynamically importing to avoid any complications with route in auth.ts
            import("../helpers/auth").then(({ redirectAuthenticatedUserToLogout }) => {
              return redirectAuthenticatedUserToLogout({ connectedAsAdministrator: false });
            });
          }

          if (err instanceof TRPCClientError) {
            // We type assert here to match the inferred
            // type of the trpc endpoints in the client.
            const trpcError = err as TRPCClientError<TRouter> & {
              normalizedError?: TRPCErrorData<unknown> | null;
            };
            const parsedError = assertTRPCErrorData(trpcError.data);
            trpcError.normalizedError = parsedError?.error ?? null;

            if (op.type === "mutation" && parsedError) {
              observer.next({ result: { data: parsedError } });
              return;
            }

            observer.error(trpcError);
          } else {
            observer.error(err as never);
          }
        },
        complete: () => {
          observer.complete();
        },
      });
    });
  };
};

const applyRecaptchaHeadersLink = <TRouter extends AnyRouter>(): OperationLink<TRouter> => {
  return ({ next, op }) => {
    return observable((observer) => {
      // Apply recaptcha headers to the request if they are present and remove them from the input
      if (op.input && typeof op.input === "object" && "recaptchaHeaders" in op.input) {
        const { recaptchaHeaders, ...rest } = op.input as TRPCRequestOptions & {
          recaptchaHeaders?: typeof RECAPTCHA_HEADERS;
        };
        op.context = {
          ...op.context,
          headers: {
            ...(op.context?.headers || {}),
            ...recaptchaHeaders,
          },
        };
        op.input = rest;
      }
      return next(op).subscribe({
        next(value) {
          observer.next(value);
        },
        error(err) {
          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });
    });
  };
};

const dynamicHeadersLink = <TRouter extends AnyRouter>(): OperationLink<TRouter> => {
  return ({ op, next }) => {
    // Inject all other custom headers from input
    if (op.input && typeof op.input === "object" && "headers" in op.input) {
      const { headers, ...rest } = op.input as TRPCRequestOptions & {
        headers?: Record<string, string>;
      };
      op.context = {
        ...op.context,
        headers: {
          ...(op.context?.headers || {}),
          ...headers,
        },
      };
      op.input = rest;
    }
    // Pass the updated context to the next link
    return next(op);
  };
};

export const trpc = createTRPCNext<AppRouterType>({
  ssr: (opts): boolean => {
    // only SSR if the request is coming from a bot. https://trpc.io/docs/v10/client/nextjs/ssr
    return !!opts.ctx?.req?.headers["user-agent"]?.includes("bot");
  },
  config(opts) {
    const { ctx } = opts;
    return {
      links: [
        errorLink,
        applyRecaptchaHeadersLink,
        dynamicHeadersLink,
        httpLink({
          url: process.env.NEXT_PUBLIC_API_URL + SERVER_TRPC_BASE_PATH,
          headers({ op }) {
            // Extract existing headers from context and add them to the request
            const contextHeaders = op.context?.headers as Record<string, string> | undefined;
            return {
              ...contextHeaders,
              cookie: !ctx?.req?.headers ? contextHeaders?.cookie : ctx.req.headers.cookie,
            };
          },
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: "include",
            });
          },
        }),
      ],
      queryClientConfig: {
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      },
    };
  },
  responseMeta(opts) {
    const { clientErrors } = opts;
    if (clientErrors.length) {
      // propagate first http error from API calls
      return {
        status: clientErrors[0].data?.httpStatus ?? 500,
      };
    }
    // cache full page for 1 day + revalidate once every second
    const ONE_DAY_IN_SECONDS = 60 * 60 * 24;
    return {
      "Cache-Control": `s-maxage=1, stale-while-revalidate=${ONE_DAY_IN_SECONDS}`,
    };
  },
});
